import {
  PRODUCT_NAME, PRODUCT_SUBTITLE, HERO_TITLE_LEAD, HERO_TITLE_REST, HERO_BLURB,
  FEATURES, DPIIT, COMPANY_LINE, RIGHTS_LINE, MADE_IN, MADE_FOR, LOGO_SRC,
} from "@/lib/brand";
import { Landmark, Lock } from "lucide-react";

// Left dark brand panel — REASON branding. Shared by Login / Forgot / Reset screens.
export function BrandPanel() {
  return (
    <div
      className="hidden lg:flex flex-col justify-between w-1/2 xl:w-[55%] text-white px-10 xl:px-14 py-10 relative overflow-hidden"
      style={{ background: "radial-gradient(1200px 600px at 80% 90%, #0c2a4d 0%, #08122e 45%, #060d22 100%)" }}
      data-testid="login-brand-panel"
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-16 bottom-24 h-72 w-72 rounded-full bg-teal-400/10 blur-3xl" />

      {/* Logo lockup */}
      <div className="relative z-10 flex items-center gap-4">
        <div className="bg-white rounded-xl p-2 shadow-lg shrink-0">
          <img src={LOGO_SRC} alt="REASON" className="h-11 w-11 object-contain" />
        </div>
        <div className="h-10 w-px bg-white/15" />
        <div>
          <div className="font-extrabold text-xl tracking-tight leading-none">{PRODUCT_NAME}</div>
          <div className="text-[11px] text-cyan-200/80 mt-1">{PRODUCT_SUBTITLE}</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 max-w-xl">
        <h1 className="text-3xl xl:text-4xl font-extrabold leading-tight">
          {HERO_TITLE_LEAD}
          <br />
          <span style={{ color: "#19c8b6" }}>{HERO_TITLE_REST}</span>
        </h1>
        <p className="text-slate-300/90 mt-5 text-sm xl:text-[15px] leading-relaxed">{HERO_BLURB}</p>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-6 mt-8">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full border border-cyan-400/30 bg-cyan-400/5 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-semibold text-[13.5px] leading-tight">{f.title}</div>
                  <div className="text-[12px] text-slate-400 mt-0.5 leading-snug">{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* DPIIT + footer */}
      <div className="relative z-10 space-y-5">
        <div className="rounded-xl border border-amber-500/40 bg-white/[0.03] px-5 py-4 max-w-xl flex items-start gap-4">
          <div className="h-11 w-11 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Landmark className="h-6 w-6 text-amber-400" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-bold text-amber-400 text-[15px]">{DPIIT.title}</div>
            <div className="text-[12.5px] text-slate-300 mt-0.5">{DPIIT.ministry}</div>
            {(DPIIT.certificateNo || DPIIT.validUntil) && (
              <div className="text-[11.5px] text-slate-400 mt-1.5">
                {DPIIT.certificateNo && <span>Certificate No: {DPIIT.certificateNo}</span>}
                {DPIIT.certificateNo && DPIIT.validUntil && <span className="mx-2">|</span>}
                {DPIIT.validUntil && <span>Valid up to: {DPIIT.validUntil}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="text-[12px] text-slate-400 flex items-center gap-2 flex-wrap">
          <Lock className="h-3.5 w-3.5" /> {COMPANY_LINE}
          <span className="text-slate-600">|</span> {RIGHTS_LINE}
        </div>
        <div className="text-[13px]">
          <span className="text-slate-300 font-medium">{MADE_IN}</span>{" "}
          <span style={{ color: "#19c8b6" }} className="font-semibold">{MADE_FOR}</span>
        </div>
      </div>
    </div>
  );
}
